import { Locale } from "../enums/general"
import { COUNTRY_CODES, COUNTRY_CODE_ALPHA2, COUNTRY_CODE_ALPHA3, COUNTRY_CODE_NUMERIC } from "./data/COUNTRY_CODES"
import type { Parseable } from "./parseable"

export type CountryCodeAlpha2 = (typeof COUNTRY_CODE_ALPHA2)[number]
export type CountryCodeAlpha3 = (typeof COUNTRY_CODE_ALPHA3)[number]
export type CountryCodeNumeric = (typeof COUNTRY_CODE_NUMERIC)[number]

/**
 *  A class describing a country code. It operates on ISO 3166-1 alpha-2 codes.
 *  It is used to represent a country code in a consistent way across the system.
 *
 *  @see https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes
 */
export class CountryCode {
  /**
   *  Get all the alpha-2 codes of the countries supported by the system. This will
   *  be mostly used to display all the countries in the frontend or used
   *  as a type for different components.
   */
  static get alpha2Codes() {
    return COUNTRY_CODE_ALPHA2
  }

  /**
   *  The alpha-2 code of the country.
   */
  private _alpha2: CountryCodeAlpha2

  /**
   *  The alpha-3 code of the country.
   */
  private _alpha3: CountryCodeAlpha3

  /**
   *  The numeric code of the country. It's stored as a string cause we aren't
   *  suppose to make numeric operations on it.
   */
  private _numeric: CountryCodeNumeric

  /**
   *  The name of the country associated with the code.
   */
  private _country: string

  /**
   *  Get the name of the country associated with the code.
   */
  get country() {
    return this._country
  }

  /**
   *  Get the alpha-2 code of the country.
   */
  get alpha2(): CountryCodeAlpha2 {
    return this._alpha2
  }

  /**
   *  Get the alpha-3 code of the country.
   */
  get alpha3(): CountryCodeAlpha3 {
    return this._alpha3
  }

  /**
   *  Get the numeric code of the country.
   */
  get numeric(): CountryCodeNumeric {
    return this._numeric
  }

  /**
   * Guess a country code from a (partial) localized country name. Checks EN
   * and NL display names for a case-insensitive prefix match, then falls back
   * to {@link CountryCode.parse} for direct code lookups (e.g. "NL", "NLD").
   */
  static guess(input: string, codeFallback: boolean = true): CountryCode | undefined {
    const trimmed = input?.trim()
    if (!trimmed) {
      return undefined
    }

    const normalizedInput = trimmed.toLowerCase()

    for (const locale of Object.values(Locale)) {
      const displayNames = new Intl.DisplayNames([locale], { type: "region" })

      for (const alpha2 of COUNTRY_CODE_ALPHA2) {
        const localizedName = displayNames.of(alpha2)?.toLowerCase()
        if (localizedName?.startsWith(normalizedInput)) {
          return new CountryCode(alpha2)
        }
      }
    }

    return codeFallback ? CountryCode.parse(trimmed) : undefined
  }

  /**
   *  Safely parse a string into a country code. Returns undefined if the string is invalid.
   */
  static parse(code: CountryCode | string | undefined): CountryCode | undefined {
    if (code instanceof CountryCode) {
      return code
    }

    if (!code) {
      return undefined
    }

    try {
      return new CountryCode(code)
    } catch {
      return undefined
    }
  }

  /**
   *  Parse a string into a country code. If the string isn't a valid country code, an error is thrown.
   */
  constructor(code: string) {
    const parsed = code.trim().toUpperCase()

    if (parsed in COUNTRY_CODES) {
      const countryCode = COUNTRY_CODES[parsed as keyof typeof COUNTRY_CODES]

      // assign the values
      this._alpha2 = countryCode.alpha2 as CountryCodeAlpha2
      this._alpha3 = countryCode.alpha3 as CountryCodeAlpha3
      this._numeric = countryCode.numeric as CountryCodeNumeric
      this._country = countryCode.name
    } else {
      throw new Error(`Invalid country code: ${code}`)
    }
  }

  /**
   *  Get a localized name of the country of the country code.
   */
  getLocalizedName(locale: Locale): string {
    // The code of the country that we want to find.
    const code = this._alpha2

    // If we don't have a custom localization, we use the Intl tool to get the localized name.
    // If that fails for some reason, we return the default name.
    const displayNames = new Intl.DisplayNames([locale], { type: "region" })
    return displayNames.of(code) ?? this._country
  }

  /**
   *  Get a string representation of the country code.
   */
  toString() {
    return this._alpha2.toUpperCase()
  }
}

const _parseable: Parseable<CountryCode> = CountryCode
void _parseable
